import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination
} from '@mui/material';
import { Add, Search, Edit, Delete, Close } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';
import './TempLabor.css';

const TempLabor = () => {
  const { user } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tempLaborCost: '',
    tempLaborOTCost: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dialogMessage, setDialogMessage] = useState({ text: '', type: '' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);

  useEffect(() => {
    fetchTempLabors();
  }, []);

  const fetchTempLabors = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.TEMP_LABORS);
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.temp_labor_id,
        name: item.temp_labor_name,
        tempLaborCost: item.temp_labor_cost,
        tempLaborOTCost: item.temp_labor_ot_cost,
        modifiedDt: item.modified_dt
      }));
      setAllRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      setMessage({ text: 'Failed to load temp labors', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const rowToEdit = filteredRows.find(row => row.id === id);
    setFormData({
      name: rowToEdit.name,
      tempLaborCost: rowToEdit.tempLaborCost.toString(),
      tempLaborOTCost: rowToEdit.tempLaborOTCost.toString()
    });
    setEditMode(true);
    setEditId(id);
    setOpen(true);
  };

  const handleRowSelect = (id, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const visibleIds = new Set(paginatedRows.map(row => row.id));
      setSelectedRows(visibleIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredRows(allRows);
      return;
    }

    const filtered = allRows.filter(row => 
      row.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRows(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Temp labor name is required';
    } else if (!/^[a-zA-Z0-9\s]+$/.test(formData.name)) {
      newErrors.name = 'Invalid characters in temp labor name';
    }
    
    if (!formData.tempLaborCost.trim()) {
      newErrors.tempLaborCost = 'Regular hourly rate is required';
    } else if (isNaN(formData.tempLaborCost) || parseFloat(formData.tempLaborCost) < 0 || parseFloat(formData.tempLaborCost) > 10000) {
      newErrors.tempLaborCost = 'Invalid hourly rate (0.00 - 10000.00)';
    }
    
    if (!formData.tempLaborOTCost.trim()) {
      newErrors.tempLaborOTCost = 'Overtime rate is required';
    } else if (isNaN(formData.tempLaborOTCost) || parseFloat(formData.tempLaborOTCost) < 0 || parseFloat(formData.tempLaborOTCost) > 10000) {
      newErrors.tempLaborOTCost = 'Invalid overtime rate (0.00 - 10000.00)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setDialogMessage({ text: '', type: '' });

    if (!validateForm()) {
      setDialogMessage({ text: 'Please fix validation errors', type: 'error' });
      return;
    }

    setSaveLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const url = editMode ? `${API_ENDPOINTS.TEMP_LABORS}/${editId}` : API_ENDPOINTS.TEMP_LABORS;
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          tempLaborCost: parseFloat(formData.tempLaborCost),
          tempLaborOTCost: parseFloat(formData.tempLaborOTCost),
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          },
          modifiedDt: editMode ? filteredRows.find(row => row.id === editId)?.modifiedDt : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} temp labor`);
      }

      await fetchTempLabors();
      setOpen(false);
      handleClear();
      setMessage({ text: `Temp labor ${editMode ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setDialogMessage({ text: error.message, type: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      name: '',
      tempLaborCost: '',
      tempLaborOTCost: ''
    });
    setErrors({});
    setDialogMessage({ text: '', type: '' });
    setEditMode(false);
    setEditId(null);
  };

  const handleDeleteClick = () => {
    if (!selectedRows || selectedRows.size === 0) {
      setMessage({ text: 'Please select items to delete', type: 'warning' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSingle = (id) => {
    setDeleteRowId(id);
    setSingleDeleteConfirmOpen(true);
  };

  const handleSingleDeleteConfirm = async () => {
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(`${API_ENDPOINTS.TEMP_LABORS}/${deleteRowId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete temp labor');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      await fetchTempLabors();
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete temp labor', type: 'error' });
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const selectedIds = Array.from(selectedRows);
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(API_ENDPOINTS.TEMP_LABORS, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectionModel: selectedIds,
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete temp labors');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      setSelectedRows(new Set());
      await fetchTempLabors();
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete temp labors', type: 'error' });
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const paginatedRows = filteredRows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Temp Labor Administration</Typography>

      {(loading || saveLoading || deleteLoading) && <LinearProgress sx={{ mb: 2 }} />}
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="outlined" startIcon={<Search />} onClick={handleSearch}>
            Search
          </Button>
          {filteredRows.length !== allRows.length && (
            <Button variant="outlined" onClick={() => { setFilteredRows(allRows); setSearchTerm(''); }}>
              Show All
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />} 
            onClick={handleDeleteClick}
            disabled={!selectedRows || selectedRows.size === 0 || deleteLoading}
          >
            Delete ({selectedRows?.size || 0})
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditMode(false); setEditId(null); setOpen(true); }}>
            Add Temp Labor
          </Button>
        </Box>
      </Box>

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selectedRows.size > 0 && selectedRows.size < paginatedRows.length}
                      checked={paginatedRows.length > 0 && paginatedRows.every(row => selectedRows.has(row.id))}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{ color: 'white' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                    Temp Labor Name
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                    Regular Hourly Rate ($)
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                    Overtime Rate ($)
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => {
                  const isItemSelected = selectedRows.has(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd !important' }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        sx={{ fontSize: '13px', padding: '8px 16px' }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                        {parseFloat(row.tempLaborCost).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                        {parseFloat(row.tempLaborOTCost).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '8px 16px' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Edit
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => handleEdit(row.id)}
                          />
                          <Delete
                            sx={{ cursor: 'pointer', color: 'error.main' }}
                            onClick={() => handleDeleteSingle(row.id)}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={(event, newPage) => setPaginationModel({ ...paginationModel, page: newPage })}
            onRowsPerPageChange={(event) => setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) })}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Temp Labor' : 'Add Temp Labor'}
          <Close sx={{ cursor: 'pointer' }} onClick={() => { setOpen(false); handleClear(); }} />
        </DialogTitle>
        <DialogContent>
          {dialogMessage.text && (
            <Alert severity={dialogMessage.type} sx={{ mb: 2 }}>
              {dialogMessage.text}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Temp Labor Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Enter temp labor name"
            />
            <TextField
              fullWidth
              label="Regular Hourly Rate *"
              type="number"
              inputProps={{ min: 0, max: 10000, step: 0.01 }}
              value={formData.tempLaborCost}
              onChange={(e) => setFormData({ ...formData, tempLaborCost: e.target.value })}
              error={!!errors.tempLaborCost}
              helperText={errors.tempLaborCost}
              placeholder="0.00"
            />
            <TextField
              fullWidth
              label="Overtime Rate *"
              type="number"
              inputProps={{ min: 0, max: 10000, step: 0.01 }}
              value={formData.tempLaborOTCost}
              onChange={(e) => setFormData({ ...formData, tempLaborOTCost: e.target.value })}
              error={!!errors.tempLaborOTCost}
              helperText={errors.tempLaborOTCost}
              placeholder="0.00"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} disabled={saveLoading}>Clear</Button>
          <Button onClick={() => { setOpen(false); handleClear(); }} disabled={saveLoading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedRows?.size || 0} selected temp labor{(selectedRows?.size || 0) > 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={singleDeleteConfirmOpen} onClose={() => setSingleDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this temp labor?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteConfirmOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleSingleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TempLabor;